import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  PERMANENT_DELETE_CONFIRMATION,
  isPermanentDeleteConfirmed,
  permanentlyDeletePatient,
} from '../services/permanent-delete-patient-service';

/**
 * PRD.328 — permanent deletion confirmation.
 *
 * Deliberately thin: it collects the typed confirmation and calls the service. No
 * deletion logic lives here (PRD.328 §21 / Phase 2 §1). The service issues exactly one
 * `DELETE FROM patients`; syncWrite performs the cascade.
 *
 * The dialog asks for the literal word `delete`, not the patient's name or MRN — the
 * PRD is explicit, and typing a name is a weaker check anyway since it is on screen.
 */

/** Minimal shape of the PowerSync handle the dialog forwards to the service. */
interface Db {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
}

export interface PermanentDeletePatientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  db: Db | null;
  patientId: string;
  patientName: string;
  /** Called once the local delete succeeded, so the caller can close the workspace. */
  onDeleted: () => void;
  brandTokens?: CSSProperties;
}

export function PermanentDeletePatientDialog({
  open,
  onOpenChange,
  db,
  patientId,
  patientName,
  onDeleted,
  brandTokens,
}: PermanentDeletePatientDialogProps) {
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A reopened dialog must never start with the previous confirmation still typed.
  useEffect(() => {
    if (open) {
      setConfirmation('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const canSubmit = isPermanentDeleteConfirmed(confirmation) && !busy && !!db;

  const handleDelete = useCallback(async () => {
    if (!db || busy) return; // guards double submission (PRD.328 §2)
    setBusy(true);
    setError(null);
    const result = await permanentlyDeletePatient({ db, patientId, confirmation });
    if (!result.deleted) {
      setBusy(false);
      setError(result.error ?? 'Deletion failed.');
      return;
    }
    onOpenChange(false);
    onDeleted();
  }, [db, busy, patientId, confirmation, onOpenChange, onDeleted]);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (busy) return; // no dismissing mid-flight
        onOpenChange(next);
      }}
      title="Delete patient permanently?"
      maxWidth={460}
      portalStyle={brandTokens}
      closeOnBackdrop={!busy}
      closeOnEsc={!busy}
      showCloseButton={!busy}
      footer={
        <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
          <Button variant="outline" size="md" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <button
            onClick={handleDelete}
            disabled={!canSubmit}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 36,
              padding: '0 var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: canSubmit ? 'var(--color-danger)' : 'var(--surface-muted)',
              color: canSubmit ? '#fff' : 'var(--text-placeholder)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--font-family)',
            }}
          >
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      }
    >
      <div style={{ padding: 'var(--sp-4) var(--sp-6)', display: 'grid', gap: 'var(--sp-4)' }}>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm)',
            lineHeight: 'var(--line-height-sm)',
          }}
        >
          <strong>{patientName}</strong> and every record belonging to them will be permanently
          erased: visits and consultations, prescriptions, invoices and payments, laboratory
          results, forms, files, images and voice recordings.
        </p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--color-danger)',
            fontWeight: 'var(--font-weight-semi)',
          }}
        >
          This cannot be undone. The records cannot be recovered.
        </p>
        <label style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>
            Type <strong>{PERMANENT_DELETE_CONFIRMATION}</strong> to confirm
          </span>
          <input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            disabled={busy}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-label={`Type ${PERMANENT_DELETE_CONFIRMATION} to confirm permanent deletion`}
            style={{
              height: 36,
              padding: '0 var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family)',
              background: busy ? 'var(--surface-muted)' : 'var(--surface)',
              color: 'var(--text)',
            }}
          />
        </label>
        {error && (
          <p
            role="alert"
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
